import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import { Building2, Plus, Search, Pencil, Power, PowerOff, Lock, UserPlus, ShieldCheck, CheckCircle2 } from 'lucide-react';
import partnerAdminService from '../../services/partnerAdminService';
import { usePermissions } from '../../context/PermissionsContext';
import { PARTNER_PERMISSIONS } from '../../constants/permissions';
import { PARTNER_ADMIN_COPY } from '../../constants/partnerCopy';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { apiErrorText, describeApiError } from '../../utils/apiError';
import PaginationBar from '../PlatformAdmin/PaginationBar';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

// Company names are unique platform-wide; the backend deliberately says only
// "That name is not available" so it never reveals another customer (§5.2).
const isNameTaken = (err) => err?.status === 400 && /not available/i.test(apiErrorText(err));

/**
 * The partner's client companies (Partner tier guide §5.2): list, create,
 * rename and (de)activate. The home company never appears here.
 */
const PartnerCompaniesPanel = ({ ctx, notify }) => {
  const { hasPartner } = usePermissions();
  const canUsers = hasPartner(PARTNER_PERMISSIONS.USERS);
  const canLimits = hasPartner(PARTNER_PERMISSIONS.ENTITLEMENTS);
  const { reloadCompanies, reloadMe, partnerName } = ctx;

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [activeOnly, setActiveOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState(null); // the new company → next-steps view
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerAdminService.listCompanies({
        activeOnly,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      const list = data?.companies || (Array.isArray(data) ? data : []);
      setCompanies(list);
      setTotal(Number.isFinite(data?.total) ? data.total : list.length);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load companies.'));
    } finally {
      setLoading(false);
    }
  }, [activeOnly, debouncedSearch, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = async () => {
    await load();
    await Promise.all([reloadCompanies(), reloadMe()]);
  };

  const handleFailure = async (err, fallback) => {
    if (err?.status === 404) {
      notify(PARTNER_ADMIN_COPY.noLongerInPartner(partnerName), 'warning');
      await afterChange();
      return;
    }
    notify(describeApiError(err, fallback), 'error');
  };

  const openCreate = () => {
    setName('');
    setNameError(null);
    setCreated(null);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (busy) return;
    setCreateOpen(false);
    setCreated(null);
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      setBusy(true);
      setNameError(null);
      const res = await partnerAdminService.createCompany({ name: trimmed, is_active: true });
      setCreated(res?.company || res || { name: trimmed });
      await afterChange();
    } catch (err) {
      if (isNameTaken(err)) setNameError(PARTNER_ADMIN_COPY.nameTaken);
      else notify(describeApiError(err, 'Failed to create the company.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async () => {
    const trimmed = name.trim();
    if (!renameTarget || !trimmed) return;
    if (trimmed === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    try {
      setBusy(true);
      setNameError(null);
      await partnerAdminService.updateCompany(renameTarget.id, { name: trimmed });
      notify('Company renamed.', 'success');
      setRenameTarget(null);
      await afterChange();
    } catch (err) {
      if (isNameTaken(err)) setNameError(PARTNER_ADMIN_COPY.nameTaken);
      else await handleFailure(err, 'Failed to rename the company.');
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (company, isActive) => {
    try {
      setBusy(true);
      await partnerAdminService.updateCompany(company.id, { is_active: isActive });
      notify(
        isActive
          ? `${company.name} reactivated. Its users can sign in again.`
          : `${company.name} deactivated. Its users are blocked until you reactivate it.`,
        isActive ? 'success' : 'info'
      );
      setDeactivateTarget(null);
      await afterChange();
    } catch (err) {
      await handleFailure(err, 'Failed to update the company.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box className={styles.toolbar}>
        <Box className={styles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search companies…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={activeOnly ? 'active' : 'all'}
              onChange={(e) => { setActiveOnly(e.target.value === 'active'); setOffset(0); }}
            >
              <MenuItem value="all">All companies</MenuItem>
              <MenuItem value="active">Active only</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button variant="contained" startIcon={<Plus size={18} />} className={styles.primaryBtn} onClick={openCreate}>
          New company
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : companies.length === 0 ? (
        <Box className={styles.emptyState}>
          <Building2 className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No companies yet</Typography>
          <Typography variant="body2">Create your first client company to start onboarding its users.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {companies.map((company) => (
            <Box key={company.id} className={styles.userRow} sx={{ opacity: company.is_active ? 1 : 0.7 }}>
              <Box className={styles.companyMain}>
                <span className={styles.companyAvatar}>{(company.name || '?').charAt(0).toUpperCase()}</span>
                <Box>
                  <span className={styles.companyName}>{company.name}</span>
                  <span className={styles.companyMeta}>ID {company.id}</span>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className={`${styles.statusPill} ${company.is_active ? styles.active : styles.inactive}`}>
                  {company.is_active ? 'Active' : 'Inactive'}
                </span>
                <Tooltip title="Rename">
                  <IconButton
                    size="small"
                    className={styles.subtleBtn}
                    onClick={() => { setName(company.name); setNameError(null); setRenameTarget(company); }}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={company.is_active ? 'Deactivate' : 'Activate'}>
                  <IconButton
                    size="small"
                    className={styles.dangerIcon}
                    disabled={busy}
                    onClick={() => (company.is_active ? setDeactivateTarget(company) : setActive(company, true))}
                  >
                    {company.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar total={total} limit={PAGE_SIZE} offset={offset} loading={loading} unit="companies" onOffsetChange={setOffset} />
      )}

      {/* Create → next steps (§5.2) */}
      <Dialog open={createOpen} onClose={closeCreate} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>{created ? `${created.name} created` : 'New company'}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {created ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <CheckCircle2 size={20} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  It starts with every feature your plan offers. Next steps:
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {canUsers && created.id != null && (
                  <Button
                    variant="outlined"
                    className={styles.outlineBtn}
                    startIcon={<UserPlus size={16} />}
                    onClick={() => { setCreateOpen(false); ctx.navigate('users', { createForCompanyId: created.id }); }}
                  >
                    {PARTNER_ADMIN_COPY.addAdminFor(created.name)}
                  </Button>
                )}
                {canLimits && created.id != null && (
                  <Button
                    variant="outlined"
                    className={styles.outlineBtn}
                    startIcon={<ShieldCheck size={16} />}
                    onClick={() => { setCreateOpen(false); ctx.navigate('limits', { companyId: created.id }); }}
                  >
                    {PARTNER_ADMIN_COPY.reviewLimits}
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <TextField
              autoFocus
              label="Company name"
              required
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              error={!!nameError}
              helperText={nameError}
              className={styles.formField}
            />
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          {created ? (
            <Button onClick={closeCreate} className={styles.cancelButton}>Done</Button>
          ) : (
            <>
              <Button onClick={closeCreate} disabled={busy} className={styles.cancelButton}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate} disabled={busy || !name.trim()} className={styles.primaryBtn}>
                {busy ? <CircularProgress size={20} color="inherit" /> : 'Create company'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Rename */}
      <Dialog open={!!renameTarget} onClose={() => !busy && setRenameTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Rename company</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            label="Company name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(null); }}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            error={!!nameError}
            helperText={nameError}
            className={styles.formField}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRenameTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleRename} disabled={busy || !name.trim()} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate */}
      <Dialog open={!!deactivateTarget} onClose={() => !busy && setDeactivateTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Deactivate company</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteText}>
            {deactivateTarget && PARTNER_ADMIN_COPY.deactivateCompany(deactivateTarget.name)}
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setDeactivateTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => setActive(deactivateTarget, false)}
            disabled={busy}
            className={styles.dangerButton}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerCompaniesPanel;
